/**
 * React-query hooks for the GO Lab annotation endpoints (B5).
 *
 * Mirrors the conventions established in
 * `src/components/v4/golab/datasets/hooks/useDatasets.ts`:
 *   * Hierarchical `*Keys` factory under the `['golab', 'annotation']`
 *     namespace so mutations can invalidate prefixes cleanly.
 *   * 30s `staleTime` for "live counts" reads; `keepPreviousData` for
 *     smooth pagination.
 *   * `skipRetryOn404` so an unknown / wrong-org record doesn't bombard
 *     the server.
 *
 * Hooks intentionally don't show toasts — that's a per-page UX concern.
 */

import {
  keepPreviousData,
  useMutation,
  useQuery,
  useQueryClient,
} from '@tanstack/react-query';

import { skipRetryOn404 } from '../../../../../services/types/errors';
import { annotationService } from '../services/annotationService';
import type {
  CompletedTodayResponse,
  DatasetRecordDetail,
  ListPaginationParams,
  PipelineMetricsResponse,
  QueueList,
  SubmitAnnotationPayload,
  SubmitAnnotationResult,
  SubmitResolutionPayload,
  SubmitResolutionResult,
} from '../types';


/**
 * Stable query-key factory for cache invalidation. Namespaced under
 * `['golab', 'annotation']` to avoid colliding with V4's flat key spaces
 * and the sibling `['golab', 'datasets']` / `['golab', 'snapshots']`.
 */
export const annotationKeys = {
  all: ['golab', 'annotation'] as const,
  queue: () => [...annotationKeys.all, 'queue'] as const,
  queueList: (params: ListPaginationParams) =>
    [...annotationKeys.queue(), 'list', params] as const,
  reviewQueue: () => [...annotationKeys.all, 'review-queue'] as const,
  reviewQueueList: (params: ListPaginationParams) =>
    [...annotationKeys.reviewQueue(), 'list', params] as const,
  records: () => [...annotationKeys.all, 'records'] as const,
  record: (id: string) => [...annotationKeys.records(), 'detail', id] as const,
  pipelineMetrics: () => [...annotationKeys.all, 'pipeline-metrics'] as const,
  completedToday: () => [...annotationKeys.all, 'completed-today'] as const,
};


/**
 * Paginated queue of records eligible for the current user (server reads
 * `current_user.user_id` from the auth dep).
 *
 * `placeholderData: keepPreviousData` keeps the prior page visible while
 * the next one loads — smooth pagination without flicker.
 */
export const useMyQueue = (params: ListPaginationParams = {}) =>
  useQuery<QueueList>({
    queryKey: annotationKeys.queueList(params),
    queryFn: () => annotationService.getMyQueue(params),
    staleTime: 30 * 1000,
    placeholderData: keepPreviousData,
    retry: skipRetryOn404,
  });

/**
 * Org-wide review queue (B19) — mixed `agreed` + `disputed` records.
 * Replaces the prior adjudication-only queue. Row-click branches in the
 * page: disputed → `AdjudicationFlowPage` at
 * `/v4/go-lab/annotate/review/:id`; agreed → `FinaliseAgreedDialog`
 * opens in place.
 */
export const useReviewQueue = (params: ListPaginationParams = {}) =>
  useQuery<QueueList>({
    queryKey: annotationKeys.reviewQueueList(params),
    queryFn: () => annotationService.getReviewQueue(params),
    staleTime: 30 * 1000,
    placeholderData: keepPreviousData,
    retry: skipRetryOn404,
  });

/**
 * Single-record detail with `is_current` first + second + resolution.
 *
 * `enabled` defaults to `Boolean(recordId)` so consumers can pass an empty
 * id without triggering an inflight fetch (e.g. while routing).
 */
export const useDatasetRecord = (
  recordId: string,
  options?: { enabled?: boolean },
) =>
  useQuery<DatasetRecordDetail>({
    queryKey: annotationKeys.record(recordId),
    queryFn: () => annotationService.getDatasetRecord(recordId),
    enabled: options?.enabled ?? Boolean(recordId),
    staleTime: 30 * 1000,
    retry: skipRetryOn404,
  });

/**
 * Pipeline-stage counts for the global metrics strip (B18-FE). Single
 * GROUP BY on the BE (B18-BE); cheap to call. The strip renders em-dash
 * placeholders via `isLoading` / `isError` so loading + error states
 * degrade gracefully without layout shift.
 */
export const usePipelineMetrics = () =>
  useQuery<PipelineMetricsResponse, Error>({
    queryKey: annotationKeys.pipelineMetrics(),
    queryFn: () => annotationService.getPipelineMetrics(),
    staleTime: 30 * 1000,
    retry: skipRetryOn404,
  });

/**
 * Today's (UTC) completed-work counts for the current user + their org
 * (B20-BE). Powers `QueueStatStrip`'s "Completed today" card on both
 * the Annotation queue (uses `annotationSubmissions`) and the Review
 * queue (uses `finalisations`).
 *
 * `staleTime: 60_000` — longer than the 30s pipeline strip because the
 * count changes less frequently and the indicator is rougher. The
 * three mutation hooks that affect this count (`useSubmitAnnotation`,
 * `useSubmitResolution`, `useFinaliseRecord`) invalidate the cache key
 * directly, so the staleTime only matters on idle navigation.
 */
export const useCompletedToday = () =>
  useQuery<CompletedTodayResponse, Error>({
    queryKey: annotationKeys.completedToday(),
    queryFn: () => annotationService.getCompletedToday(),
    staleTime: 60 * 1000,
    retry: skipRetryOn404,
  });

// The dataset-tab caches (collection list, channel datasets,
// overview, coverage, quality) are derived from the same record
// statuses our annotation mutations move records between. Bust the
// whole golab.datasets tree on any status-changing mutation so a user
// with a warm Datasets tab cache sees the new golden / in-progress /
// disputed counts without reload. Inlined as a literal tuple to avoid
// a circular import - `useDatasets.ts` already imports `annotationKeys`
// from this file; reversing the direction would create a cycle. The
// literal mirrors `datasetKeys.all` in
// `../../datasets/hooks/useDatasets.ts:55`.
const _DATASET_KEY_ROOT = ['golab', 'datasets'] as const;

/**
 * Submit a first or second annotator pass.
 *
 * On success invalidates the user queue, adjudication queue, and this
 * record's detail — the record may flip between queues, status advances,
 * and a second-pass submit may produce `agreement` info. Cheap to bust
 * everything; list payloads are small.
 */
export const useSubmitAnnotation = (recordId: string) => {
  const qc = useQueryClient();
  return useMutation<SubmitAnnotationResult, Error, SubmitAnnotationPayload>({
    mutationFn: (payload) =>
      annotationService.submitAnnotation(recordId, payload),
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: annotationKeys.queue() });
      void qc.invalidateQueries({ queryKey: annotationKeys.reviewQueue() });
      void qc.invalidateQueries({ queryKey: annotationKeys.record(recordId) });
      // B18-FE: pipeline strip — first/second pass complete shifts
      // `awaiting` → `first_complete`/`both_complete`.
      void qc.invalidateQueries({ queryKey: annotationKeys.pipelineMetrics() });
      // B20-FE: "Completed today" — annotationSubmissions ticks up.
      void qc.invalidateQueries({ queryKey: annotationKeys.completedToday() });
      // Dataset-tab counts bucket on `status`; status moved.
      void qc.invalidateQueries({ queryKey: _DATASET_KEY_ROOT });
    },
  });
};

/**
 * Adjudicate a disputed record. On success the record transitions to
 * `finalised`; invalidate the same key set as `useSubmitAnnotation` since
 * the record's queue membership and detail both change.
 */
export const useSubmitResolution = (recordId: string) => {
  const qc = useQueryClient();
  return useMutation<SubmitResolutionResult, Error, SubmitResolutionPayload>({
    mutationFn: (payload) =>
      annotationService.submitResolution(recordId, payload),
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: annotationKeys.queue() });
      void qc.invalidateQueries({ queryKey: annotationKeys.reviewQueue() });
      void qc.invalidateQueries({ queryKey: annotationKeys.record(recordId) });
      // B18-FE: pipeline strip — resolution moves `agreed_disputed` → `golden`.
      void qc.invalidateQueries({ queryKey: annotationKeys.pipelineMetrics() });
      // B20-FE: "Completed today" — finalisations ticks up.
      void qc.invalidateQueries({ queryKey: annotationKeys.completedToday() });
      // Dataset-tab counts: disputed → finalised changes the
      // golden + disputed buckets on the Datasets surface.
      void qc.invalidateQueries({ queryKey: _DATASET_KEY_ROOT });
    },
  });
};

/**
 * Self-claim the next available record (B14-BE).
 *
 * Returns the claimed record's full detail on success, or `null` when no
 * record is claimable (BE responds 204). Consumers handle both cases —
 * the most common pattern is: on a detail response, navigate to the
 * annotation flow at `/v4/go-lab/annotate/queue/{record.id}`; on `null`,
 * show an info toast and stay on the queue page.
 *
 * On success we invalidate `annotationKeys.queue()` so the caller's
 * queue refreshes (the claim flipped a record's status; the queue's
 * row should reflect that). When a record actually came back we also
 * invalidate its detail key so the annotation flow page picks up the
 * post-claim state on its next read.
 */
export const useStartNextAvailable = () => {
  const qc = useQueryClient();
  return useMutation<DatasetRecordDetail | null, Error, void>({
    mutationFn: () => annotationService.startNextAvailable(),
    onSuccess: (data) => {
      void qc.invalidateQueries({ queryKey: annotationKeys.queue() });
      // B18-FE: pipeline strip — covers the second-claim case where
      // `annotated_first` → `assigned_second` (a `first_complete`-bucket
      // shuffle). Cheap to invalidate unconditionally.
      void qc.invalidateQueries({ queryKey: annotationKeys.pipelineMetrics() });
      if (data) {
        void qc.invalidateQueries({
          queryKey: annotationKeys.record(data.record.id),
        });
      }
    },
  });
};

/**
 * Explicit-id self-claim for a single record. Powers the AnnotationFlowPage's
 * "Claim & start annotating" gate. Curried by record id (matches the
 * `useSubmitAnnotation(recordId)` shape) so the cache invalidation can
 * target the right detail key.
 *
 * On success invalidates both the record-detail key (so the gate's
 * `useDatasetRecord(id)` re-reads and sees the user as an annotator) AND
 * the queue (the claim flipped status; the queue row should reflect it).
 */
export const useStartAnnotation = (recordId: string) => {
  const qc = useQueryClient();
  return useMutation<DatasetRecordDetail, Error, void>({
    mutationFn: () => annotationService.startAnnotation(recordId),
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: annotationKeys.record(recordId) });
      void qc.invalidateQueries({ queryKey: annotationKeys.queue() });
      // B18-FE: pipeline strip — same rationale as `useStartNextAvailable`.
      void qc.invalidateQueries({ queryKey: annotationKeys.pipelineMetrics() });
    },
  });
};

/**
 * Finalise an `agreed` record straight to the golden dataset (B19-BE).
 * Promotes the record to `finalised` server-side; both annotators already
 * agreed, so `goldLabels` were populated at the auto-agree moment. 400
 * if the record isn't in `agreed` status; 404 if cross-org / unknown.
 *
 * On success invalidates the review queue (this row leaves it), the
 * record detail (status → `finalised`), and the pipeline-counts strip
 * (`agreedDisputed` shrinks, `golden` grows). We do NOT invalidate
 * `queue()` — agreed records are past the second-pass stage so they
 * were never on any annotator's personal queue.
 *
 * Not idempotent — a retry on a now-`finalised` record returns 400, not
 * 200. The dialog closes on success so the user can't double-click.
 */
export const useFinaliseRecord = (recordId: string) => {
  const qc = useQueryClient();
  return useMutation<DatasetRecordDetail, Error, void>({
    mutationFn: () => annotationService.finaliseRecord(recordId),
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: annotationKeys.reviewQueue() });
      void qc.invalidateQueries({ queryKey: annotationKeys.record(recordId) });
      void qc.invalidateQueries({ queryKey: annotationKeys.pipelineMetrics() });
      // B20-FE: "Completed today" — finalisations ticks up.
      void qc.invalidateQueries({ queryKey: annotationKeys.completedToday() });
      // Dataset-tab counts: agreed → finalised flips the
      // golden bucket on the Datasets surface.
      void qc.invalidateQueries({ queryKey: _DATASET_KEY_ROOT });
    },
  });
};
